import {
  BranchDefinition,
  CaseDefinition,
  CaseMember,
  CaseUserDefinition,
  ChatDefinition,
  ChatMessage,
  CheckpointDefinition,
  Claim,
  Entity,
  FileAction,
  FileActionListItem,
  FileActionSummary,
  FileDefinition,
  FileMeta,
  MessageAttachment,
  PinnedItem,
  Source,
  StateDefinition,
  TextSnippetWithoutText,
} from 'src/api';
import { DemoCaseBundle } from './demo-case-bundle';
import {
  RawCase,
  RawCaseMember,
  RawChat,
  RawClaim,
  RawDemoCaseIndexEntry,
  RawEntity,
  RawFile,
  RawFileAction,
  RawFileMeta,
  RawMessage,
  RawMessageAttachment,
  RawPinnedItem,
  RawSnippetJoin,
  RawSource,
  RawState,
  RawUser,
} from './demo-export.types';

/**
 * Turns exported database rows into the API response shapes the app expects.
 *
 * Everything here mirrors a Marshmallow schema in `backend/flask/schemas/`. Where the API
 * derives a field rather than reading a column, the comment names what it mirrors, because
 * that is the code that has to change in step when the API changes.
 */

/**
 * `CaseDefinition.created_at` is a required string, but a `RawDemoCaseIndexEntry` can omit it.
 * Falling back to `''` would make `dayjs(created_at)` invalid, which `processCases` renders as
 * the literal string "Invalid Date" wherever it stands in for a missing case name, and which
 * sorts unpredictably. Falling back to the epoch keeps it a valid date and reads as "unknown",
 * sorting/grouping as the oldest possible case rather than a real one.
 */
const UNKNOWN_CREATED_AT = '1970-01-01T00:00:00.000Z';

const REASONING_BLOCK_TYPES = new Set(['reasoning', 'reasoning_content']);
const ZIP_MIME_TYPES = new Set(['application/zip', 'application/x-zip-compressed']);
/** `_LIST_FILES_CHILD_EMBED_LIMIT` in `routes/files.py`. */
const CHILD_EMBED_LIMIT = 100;

/** Snippet ids are bigints in the database but the API dumps them through a UUID field. */
const snippetId = (id: number): string => String(id);

function isTempFile(file: RawFile): boolean {
  const isTemp = file.version_info?.['is_temp'];
  return isTemp === true || isTemp === 'true';
}

/** `_file_row_not_temp_predicate` + the trashbin filter that every listing applies. */
export function isListableFile(file: RawFile): boolean {
  return !file.trashbinned_at && !isTempFile(file);
}

// ---------------------------------------------------------------------------- case

function toCaseUser(user: RawUser | null): CaseUserDefinition | null {
  if (!user) return null;
  return {
    id: user.id,
    first_name: user.first_name ?? undefined,
    last_name: user.last_name ?? undefined,
    mail: user.mail,
    role: user.role === 'ADMIN' ? 'ADMIN' : 'USER',
  };
}

function toCaseMember(member: RawCaseMember): CaseMember {
  return {
    user_id: member.user_id,
    role: member.role as CaseMember.RoleEnum,
    user: toCaseUser(member.user),
  };
}

function toPinnedItem(pinned: RawPinnedItem): PinnedItem {
  return {
    id: pinned.id,
    user_id: pinned.user_id,
    item_type: pinned.item_type,
    item_id: pinned.item_id,
    pinned_at: pinned.pinned_at,
  };
}

export function toCaseDefinition(raw: RawCase | RawDemoCaseIndexEntry): CaseDefinition {
  const full = raw as Partial<RawCase>;
  return {
    id: raw.id,
    name: raw.name ?? undefined,
    file_number: raw.file_number ?? undefined,
    // Always set on an exported case; only an index entry can be missing it.
    created_at: raw.created_at ?? UNKNOWN_CREATED_AT,
    created_by: raw.created_by ?? null,
    summary: raw.summary ?? undefined,
    summary_updated_at: full.summary_updated_at ?? undefined,
    last_opened_at: raw.last_opened_at ?? undefined,
    pinned_items: (full.pinned_items ?? []).map(toPinnedItem),
    meta: full.meta ?? null,
    sync_path: full.sync_path ?? undefined,
    embedding_engine: full.embedding_engine ?? null,
    creator: toCaseUser(raw.creator ?? null),
    members: (full.members ?? []).map(toCaseMember),
  };
}

// ---------------------------------------------------------------------------- files

/**
 * `batch_get_parent_folders`: the closest ancestor that is actually a folder, skipping
 * container files (a document extracted from a ZIP has that ZIP as `parent_file`).
 */
function resolveParentFolder(bundle: DemoCaseBundle, file: RawFile): string | null {
  let current = file.parent_file ? bundle.filesById.get(file.parent_file) : undefined;
  while (current) {
    if (current.is_folder && !current.trashbinned_at) return current.id;
    current = current.parent_file ? bundle.filesById.get(current.parent_file) : undefined;
  }
  return null;
}

function isZipArchive(file: RawFile): boolean {
  if (file.is_folder) return false;
  return (
    ZIP_MIME_TYPES.has((file.mime_type ?? '').toLowerCase()) ||
    (file.filename ?? '').toLowerCase().endsWith('.zip')
  );
}

function baseFileFields(bundle: DemoCaseBundle, file: RawFile) {
  return {
    id: file.id,
    filename: file.filename,
    mime_type: file.mime_type,
    size: file.size,
    is_folder: file.is_folder,
    uploaded_at: file.uploaded_at,
    uploaded_by: file.uploaded_by,
    parent_file: file.parent_file,
    parent_folder: resolveParentFolder(bundle, file),
    version_info: file.version_info,
    draft_status: (file.draft_status ?? 'done') as FileDefinition.DraftStatusEnum,
    analysis_status: file.analysis_status as FileDefinition.AnalysisStatusEnum | null,
    associate_state_id: file.associate_state_id,
    modified_at: file.modified_at,
    trashbinned_at: file.trashbinned_at,
    sync_path: file.sync_path ?? undefined,
    is_read: true,
  };
}

/**
 * `_enrich_list_files_child_metadata`: every row gets a `child_count`, and non-folder rows
 * additionally get their children embedded — except huge ZIP archives, which are left to be
 * paged through separately.
 */
export function toFileDefinition(bundle: DemoCaseBundle, file: RawFile): FileDefinition {
  const childIds = bundle.childFileIds(file.id);
  const listableChildren = childIds
    .map((id) => bundle.filesById.get(id))
    .filter((child): child is RawFile => !!child && isListableFile(child));

  const childCount = listableChildren.length;
  const embedChildren =
    !file.is_folder &&
    childCount > 0 &&
    !(isZipArchive(file) && childCount > CHILD_EMBED_LIMIT);

  return {
    ...baseFileFields(bundle, file),
    child_count: childCount,
    children: embedChildren
      ? [...listableChildren]
          .sort((a, b) => a.filename.localeCompare(b.filename))
          .map((child) => ({ ...baseFileFields(bundle, child), child_count: 0, children: [] }))
      : [],
  };
}

function toTextSnippet(join: RawSnippetJoin): TextSnippetWithoutText {
  return {
    id: snippetId(join.text_snippet),
    page_start: join.page_start ?? undefined,
    page_end: join.page_end ?? undefined,
    page_position: join.page_position ?? undefined,
  };
}

export function toClaim(claim: RawClaim): Claim {
  return {
    id: claim.id,
    text: claim.text,
    created_at: claim.created_at,
    meta: claim.meta ?? undefined,
    entity_id: claim.entity_id,
    claim_type_name: claim.claim_type_name,
  };
}

export function toEntity(entity: RawEntity): Entity {
  return {
    id: entity.id,
    name: entity.name,
    created_at: entity.created_at,
    meta: entity.meta ?? undefined,
    entity_type_name: entity.entity_type_name,
    claims: (entity.claims ?? []).map(toClaim),
  };
}

/** `FileMetaSchema`, whose `raw_text` is the file's snippets concatenated in order. */
export function toFileMeta(
  bundle: DemoCaseBundle,
  file: RawFile,
  meta: RawFileMeta,
): FileMeta {
  const snippets = [...meta.text_snippets].sort((a, b) => a.order - b.order);
  return {
    ...toFileDefinition(bundle, file),
    case_id: file.case_id ?? undefined,
    templates: [],
    used_credits: file.used_credits ?? 0,
    used_ocr_pages: file.used_ocr_pages ?? 0,
    raw_text: snippets.map((snippet) => snippet.text ?? '').join(''),
    entity_ref: meta.entity_ref ? toEntity(meta.entity_ref) : undefined,
    extracted_entities: meta.entities.map((join) => ({ entity: toEntity(join.entity) })),
    text_snippets: snippets.map(toTextSnippet),
  };
}

/** `FileActionSchema`, which exposes several fields derived from `action_metadata`. */
export function toFileAction(action: RawFileAction): FileAction {
  const metadata = (action.action_metadata ?? {}) as Record<string, unknown>;
  return {
    id: action.id,
    file_id: action.file_id,
    action: action.action,
    action_metadata: metadata,
    action_at: action.action_at,
    status: action.status as FileAction.StatusEnum,
    user_id: action.user_id,
    message_id: action.message_id,
    range: metadata['range'] ?? '',
    created_at: action.action_at,
    applied_at: null,
    applied_by: null,
  };
}

/** Metadata keys the by-event listing leaves out of an edit (`EDIT_CONTENT_KEYS` in `routes/states.py`). */
const EDIT_CONTENT_KEYS = ['cached_changes', 'new_text', 'original_text', 'start_line_text', 'end_line_text'];

/** `FileActionSummarySchema`: the listing's view of an action, without what an edit carries. */
export function toFileActionSummary(action: RawFileAction): FileActionSummary {
  const metadata = (action.action_metadata ?? {}) as Record<string, unknown>;
  const rest = Object.fromEntries(Object.entries(metadata).filter(([key]) => !EDIT_CONTENT_KEYS.includes(key)));
  return {
    ...toFileAction(action),
    action_metadata: rest,
    has_diff: 'cached_changes' in metadata || 'new_text' in metadata,
  };
}

export function toFileActionListItem(action: RawFileAction): FileActionListItem {
  return {
    id: action.id,
    file_id: action.file_id,
    action: action.action as FileActionListItem.ActionEnum,
    action_metadata: action.action_metadata,
    action_at: action.action_at,
    status: action.status as FileActionListItem.StatusEnum,
    user_id: action.user_id,
    // `prompt_id` is left unset on purpose: `file_actions` has no such column, so the API never
    // dumps one either. Consumers read it as a state id (the version history resolves the chat
    // from it), which `message_id` is not — filling it in only produces a lookup that hangs.
  };
}

// ---------------------------------------------------------------------------- chats & state

export function toChatDefinition(chat: RawChat): ChatDefinition {
  return {
    id: chat.id,
    name: chat.name ?? undefined,
    case_id: chat.case_id ?? undefined,
    created_at: chat.created_at,
    created_by: chat.created_by,
    last_opened_at: chat.last_opened_at ?? undefined,
    pinned_items: (chat.pinned_items ?? []).map(toPinnedItem),
    associate_state_id: chat.associate_state_id,
    used_credits: chat.used_credits,
    _legacy_content_file: chat._legacy_content_file ?? undefined,
  };
}

function toCheckpoint(raw: RawState['branches'][number]['checkpoints'][number]): CheckpointDefinition {
  return {
    id: raw.id,
    branch_id: raw.branch_id,
    timestamp: raw.timestamp,
    previous_checkpoint: raw.previous_checkpoint,
    is_leaf: raw.is_leaf,
    data: raw.data ?? {},
    active_workflow_id: raw.active_workflow_id,
    active_workflow_step_id: raw.active_workflow_step_id,
    active_workflow_trigger_event_id: raw.active_workflow_trigger_event_id,
  };
}

/** `BranchDefinitionSchema`, whose `current_checkpoint` is the branch's leaf checkpoint. */
export function toBranchDefinition(branch: RawState['branches'][number]): BranchDefinition {
  const checkpoints = [...branch.checkpoints].sort(
    (a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp),
  );
  const leaf = checkpoints.find((checkpoint) => checkpoint.is_leaf) ?? checkpoints.at(-1);

  return {
    id: branch.id,
    associate_state_id: branch.associate_state_id,
    branched_off_from_checkpoint: branch.branched_off_from_checkpoint,
    created_at: branch.created_at,
    modified_at: branch.modified_at,
    current_checkpoint: leaf ? toCheckpoint(leaf) : undefined as unknown as CheckpointDefinition,
  };
}

/**
 * `StateDefinitionSchema`. `is_running` is always false: an exported case is a finished
 * snapshot, so nothing in it can be mid-run.
 */
export function toStateDefinition(bundle: DemoCaseBundle, state: RawState): StateDefinition {
  const primaryBranch =
    state.branches.find((branch) => branch.id === state.primary_branch) ?? state.branches.at(-1);
  const chat = bundle.chatByStateId.get(state.id);

  return {
    id: state.id,
    created_at: state.created_at,
    modified_at: state.modified_at,
    case_id: state.case_id ?? bundle.caseId,
    parent_message_id: state.parent_message_id as string,
    primary_branch: primaryBranch?.id ?? null,
    is_running: false,
    chats: chat ? [toChatDefinition(chat)] : [],
    files: bundle.files
      .filter((file) => file.associate_state_id === state.id)
      .map((file) => toFileDefinition(bundle, file)),
    primary_branch_ref: primaryBranch ? toBranchDefinition(primaryBranch) : null,
  };
}

function toMessageAttachment(
  bundle: DemoCaseBundle,
  attachment: RawMessageAttachment,
): MessageAttachment {
  const file = attachment.file_id ? bundle.filesById.get(attachment.file_id) : undefined;
  return {
    id: attachment.id,
    file_id: attachment.file_id ?? undefined,
    template_id: attachment.template_id ?? undefined,
    law_paragraph_id: attachment.law_paragraph_id ?? undefined,
    court_decision_id: attachment.court_decision_id ?? undefined,
    // Templates, law paragraphs and court decisions live outside the case and are not part of
    // the export, so only file attachments can be resolved to their target here.
    file: file ? toFileDefinition(bundle, file) : undefined,
  };
}

/** `apply_message_content_visibility` in `helpers/message_content.py`. */
function applyContentVisibility(
  content: Record<string, unknown>,
  role: string,
  internal: boolean,
  isAdminViewer: boolean,
): Record<string, unknown> {
  let result = content;

  if (role === 'tool' && internal && result?.['status'] === 'success') {
    result = { ...result, content: [], redacted: true };
  }

  if (role === 'ai' && !isAdminViewer && Array.isArray(result?.['content'])) {
    result = {
      ...result,
      content: (result['content'] as unknown[]).filter(
        (block) =>
          !(
            !!block &&
            typeof block === 'object' &&
            REASONING_BLOCK_TYPES.has((block as { type?: string }).type ?? '')
          ),
      ),
    };
  }

  return result;
}

export interface MessageMapperContext {
  bundle: DemoCaseBundle;
  stateId: string;
  isAdminViewer: boolean;
  /** Messages of states spawned from this one, keyed by the message that spawned them. */
  childMessages?: ReadonlyMap<string, ChatMessage[]>;
  /** Checkpoint id -> `active_workflow_trigger_event_id`, mirroring the schema's `pre_dump`. */
  triggerEventByCheckpoint: ReadonlyMap<string, string | null>;
}

export function toChatMessage(raw: RawMessage, context: MessageMapperContext): ChatMessage {
  return {
    id: raw.id,
    creation_checkpoint_id: raw.creation_checkpoint_id as string,
    content: applyContentVisibility(
      raw.content ?? {},
      raw.role,
      raw.internal,
      context.isAdminViewer,
    ),
    role: raw.role,
    internal: raw.internal,
    sent_at: raw.sent_at,
    sent_by: raw.sent_by ?? undefined,
    finalized: raw.finalized,
    request_id: raw.request_id ?? undefined,
    branch_id: raw.branch_id,
    input_tokens: raw.input_tokens,
    output_tokens: raw.output_tokens,
    favorite: raw.favorite ?? undefined,
    tool_id: raw.tool_id ?? undefined,
    meta: raw.meta,
    attachments: (raw.attachments ?? []).map((attachment) =>
      toMessageAttachment(context.bundle, attachment),
    ),
    workflow_trigger_event_id:
      context.triggerEventByCheckpoint.get(raw.creation_checkpoint_id ?? '') ?? undefined,
    state_id: context.stateId,
    child_messages: context.childMessages?.get(raw.id) ?? [],
  };
}

// ---------------------------------------------------------------------------- sources

export function toSource(bundle: DemoCaseBundle, raw: RawSource): Source {
  const file = raw.remote_file_id ? bundle.filesById.get(raw.remote_file_id) : undefined;
  const snippetJoin =
    raw.remote_snippet_id != null
      ? bundle.knowledge.snippets.find((snippet) => snippet.text_snippet === raw.remote_snippet_id)
      : undefined;
  const claim = raw.remote_claim_id
    ? bundle.knowledge.claims.find((candidate) => candidate.id === raw.remote_claim_id)
    : undefined;
  const entity = raw.remote_entity_id
    ? bundle.knowledge.entities.find((candidate) => candidate.id === raw.remote_entity_id)
    : undefined;

  return {
    id: raw.id,
    type: raw.type as Source.TypeEnum,
    case_id: raw.case_id,
    _realm: raw._realm,
    meta: raw.meta ?? {},
    remote_file_id: raw.remote_file_id ?? undefined,
    remote_snippet_id: raw.remote_snippet_id != null ? snippetId(raw.remote_snippet_id) : undefined,
    remote_claim_id: raw.remote_claim_id ?? undefined,
    remote_entity_id: raw.remote_entity_id ?? undefined,
    remote_href: raw.remote_href ?? undefined,
    remote_template_id: raw.remote_template_id ?? undefined,
    remote_legal_book_id: raw.remote_legal_book_id ?? undefined,
    remote_legal_paragraph_id: raw.remote_legal_paragraph_id ?? undefined,
    remote_legal_court_decision_id: raw.remote_legal_court_decision_id ?? undefined,
    file: file
      ? {
          id: file.id,
          filename: file.filename,
          mime_type: file.mime_type,
          uploaded_at: file.uploaded_at,
          size: file.size,
          parent_file: file.parent_file ?? undefined,
          is_folder: file.is_folder,
          draft_status: (file.draft_status ?? 'done') as FileDefinition.DraftStatusEnum,
        }
      : undefined,
    snippet: snippetJoin ? toTextSnippet(snippetJoin) : undefined,
    claim: claim ? toClaim(claim) : undefined,
    entity: entity ? toEntity(entity) : undefined,
  };
}
